import { IconPencilOff } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { type SystemPlan } from '@cfa/pathway-fe-shared/xp';
import { Button, Tooltip } from '@cfa/react-components';

import { mapCategoryToAliasTranslation } from '@/utils/categoryUtils';
import { itemByLanguage } from '@/utils/language';

import PlanCardCategoryIcon from '@/components/Cards/PlanCard/PlanCardContent/PlanCardCategoryIcon';
import { PathwayCard } from '@/components/pathway-card';

interface TemplateCardProps {
  onCopy: () => void;
  onPreview: (id: string) => void;
  plan: SystemPlan;
  onAdd: () => void;
  isLoading?: boolean;
}

export const TemplateCard: React.FC<TemplateCardProps> = ({
  onCopy,
  onPreview,
  plan,
  onAdd,
  isLoading,
}) => {
  const { t } = useTranslation();
  const isAlreadyAdded = plan.readonlySubscriptionSettings?.optedIn === true;

  return (
    <PathwayCard.Root
      noHover
      className="animate-fade-in"
      data-testid="TemplatePlanCard"
    >
      <PathwayCard.Media>
        <PlanCardCategoryIcon category={plan.category} />
      </PathwayCard.Media>
      <PathwayCard.Body>
        <PathwayCard.Label>
          {t(mapCategoryToAliasTranslation(plan.category))}
        </PathwayCard.Label>
        <PathwayCard.Title>{itemByLanguage(plan.name)}</PathwayCard.Title>
        <PathwayCard.Subtitle data-testid="PlanTaskCount">
          {`${plan.totalSteps} ${t('Generic.items')}`}
        </PathwayCard.Subtitle>
      </PathwayCard.Body>
      <PathwayCard.Meta className="gap-2 pb-0 md:gap-4">
        {plan.readonly && (
          <Tooltip
            content={t('TrainingPlans.templates.readonlyTooltip')}
            placement="bottom-end"
            showOnElementEvents={['hover']}
          >
            <IconPencilOff
              aria-label={t('TrainingPlans.templates.readonlyTooltip')}
              className="text-grayscale-gray3! hidden shrink-0! md:block"
              size={24}
            />
          </Tooltip>
        )}
        <Button
          color="secondary"
          className="w-24! min-w-24! md:min-w-20!"
          data-testid="templatePreviewButtonTest"
          onClick={() => {
            if (plan.id) {
              onPreview(plan.id);
            }
          }}
          variant="outlined"
        >
          {t('Button.preview')}
        </Button>
        <Button
          disabled={plan.readonly && isAlreadyAdded}
          loading={isLoading}
          className="w-[88px]! min-w-[88px]! md:min-w-16!"
          color="secondary"
          data-testid="templateCopyButtonTest"
          onClick={plan.readonly ? onAdd : onCopy}
          variant="filled"
        >
          {plan.readonly
            ? isAlreadyAdded
              ? t('Button.added')
              : t('Button.add')
            : t('Button.copy')}
        </Button>
      </PathwayCard.Meta>
    </PathwayCard.Root>
  );
};
